/**
 * Access Token Service
 *
 * Personal access tokens — the credential someone uses to drive the platform
 * from an outside AI client (Claude Code, Codex, any MCP host) as themselves.
 *
 * The raw token is returned by `create` and never again. Nothing here caches
 * it: it goes straight to the component that shows it once, and is gone as
 * soon as that component unmounts.
 */

import api from '@services/api';

/** What a token may do. Scopes only ever NARROW what its owner can do. */
export type TokenScope = 'read' | 'write' | 'chat';

export interface AccessToken {
  token_id: string;
  token_name: string;
  /** First 12 characters, for identifying a token without being one. */
  token_prefix: string;
  scopes: TokenScope[];
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string | null;
  is_live: boolean;
}

export interface CreatedAccessToken {
  /** Shown once. Unrecoverable afterwards — only its hash is stored. */
  token: string;
  token_info: AccessToken;
  warning: string;
}

/** What the person calls the platform, as the server resolves it. */
export interface AppIdentity {
  name: string;
  id: string;
}

export interface TokenList {
  tokens: AccessToken[];
  app: AppIdentity;
}

export interface CreateTokenRequest {
  name: string;
  scopes?: TokenScope[];
  ttl_days?: number;
}

class AccessTokenService {
  async list(includeRevoked = false): Promise<TokenList> {
    const response = await api.get('/auth/tokens', {
      params: includeRevoked ? { include_revoked: 'true' } : undefined,
    });
    return { tokens: response.data?.tokens || [], app: response.data?.app };
  }

  async create(payload: CreateTokenRequest): Promise<CreatedAccessToken> {
    const response = await api.post('/auth/tokens', payload);
    return response.data;
  }

  async revoke(tokenId: string): Promise<void> {
    await api.delete(`/auth/tokens/${tokenId}`);
  }
}

export const accessTokenService = new AccessTokenService();
export default accessTokenService;
